/**
 * MenuParallax — sticky two-column section where featured menu cards
 * scroll (parallax) through a clipping window on the right while the
 * heading, tagline, and CTA stay pinned on the left via CSS position:sticky.
 *
 * No GSAP pin is used — the outer section is made tall enough that the
 * sticky container dwells in the viewport long enough to scrub all cards.
 * This is Lenis-compatible by design.
 *
 * Registered as: menu / menu-parallax
 */
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface FeaturedItem {
  name: string;
  description: string;
  price: string;
  imageUrl?: string;
  badge?: string;
}

interface MenuParallaxProps {
  eyebrow?: string;
  headline?: string;
  tagline?: string;
  ctaLabel?: string;
  ctaTarget?: string;
  featuredItems?: FeaturedItem[];
}

const DEFAULT_ITEMS: FeaturedItem[] = [
  {
    name: "Margherita",
    description: "Fresh mozzarella, heirloom tomatoes, fresh basil & salt. Blistered to order in the wood-fire.",
    price: "$10",
  },
  {
    name: "Cubano Flatbread",
    description: "Mac sauce, pork, ham, pickles, mozzarella, crispy onion straws, sweet heat mustard drizzle.",
    price: "$12",
    badge: "Chef's Pick",
  },
  {
    name: "Copper Mule",
    description: "House-made ginger beer, vodka, fresh lime, served in our signature copper mug.",
    price: "$11",
  },
];

export function MenuParallax({ props }: SectionComponentProps) {
  const {
    eyebrow = "From Our Kitchen",
    headline = "The Menu",
    tagline = "Wood-fired, hand-crafted, made to order.",
    ctaLabel = "View Full Menu",
    ctaTarget = "/menu",
    featuredItems = DEFAULT_ITEMS,
  } = props as MenuParallaxProps;

  const sectionRef = useRef<HTMLElement>(null);
  const items = featuredItems.slice(0, 3);

  // Section is (N + 1) × 100vh tall so the sticky container dwells long enough
  // to scrub through all cards at ~0.67× scroll speed (parallax feel).
  const sectionVh = (items.length + 1) * 100;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const stack = el.querySelector<HTMLElement>(".mpc-stack");
      if (!stack) return;

      // Scrub card stack upward through the clipping window.
      // End state: last card centered in the window.
      gsap.to(stack, {
        y: () => -((items.length - 1) * window.innerHeight),
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "bottom bottom",
          scrub: 1.5,
          invalidateOnRefresh: true,
          onUpdate: (self) => updateDots(el, self.progress),
        },
      });

      // Counter-parallax on the blurred backdrop only — the sharp foreground
      // photo (object-contain) never moves, so the dish is always fully visible.
      el.querySelectorAll<HTMLElement>(".mpc-card-bgblur").forEach((bg) => {
        gsap.fromTo(
          bg,
          { yPercent: -8 },
          {
            yPercent: 8,
            ease: "none",
            scrollTrigger: {
              trigger: el,
              start: "top top",
              end: "bottom bottom",
              scrub: 3,
              invalidateOnRefresh: true,
            },
          }
        );
      });

      // Left panel: stagger entry from left on scroll into view.
      gsap.fromTo(
        el.querySelectorAll(".mpc-left > *"),
        { opacity: 0, x: -28 },
        {
          opacity: 1,
          x: 0,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: el,
            start: "top 78%",
            toggleActions: "play none none none",
          },
        }
      );
    }, el);

    return () => ctx.revert();
  }, [items.length]);

  return (
    <section
      ref={sectionRef}
      className="relative bg-background"
      style={{ height: `${sectionVh}vh` }}
      id="menu"
    >
      {/* Sticky viewport — 100vh, stays fixed while section scrolls */}
      <div
        className="sticky top-0 overflow-hidden"
        style={{ height: "100vh", display: "grid", gridTemplateColumns: "2fr 3fr" }}
      >
        {/* ── LEFT: pinned content panel ──────────────────────────────── */}
        <div
          className="mpc-left relative z-10 flex flex-col justify-center gap-6 px-10 md:px-16 bg-background"
          style={{ borderRight: "1px solid rgba(184,118,42,0.18)" }}
        >
          <span
            className="text-[10px] font-bold uppercase"
            style={{ letterSpacing: "0.32em", color: "var(--color-accent)" }}
          >
            {eyebrow}
          </span>

          <h2
            style={{
              fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
              fontStyle: "italic",
              fontWeight: 600,
              fontSize: "clamp(3rem, 5.5vw, 5rem)",
              lineHeight: 0.92,
              color: "var(--color-foreground)",
            }}
          >
            {headline}
          </h2>

          <p
            className="text-sm leading-relaxed max-w-[260px]"
            style={{ color: "var(--color-muted)" }}
          >
            {tagline}
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-2">
            {items.map((_, i) => (
              <span
                key={i}
                className="mpc-dot block rounded-full"
                style={{
                  height: 7,
                  width: i === 0 ? 20 : 7,
                  background: i === 0 ? "var(--color-primary)" : "var(--color-muted)",
                  opacity: i === 0 ? 1 : 0.35,
                  transition: "all 0.35s ease",
                }}
              />
            ))}
          </div>

          <a
            href={ctaTarget}
            className="inline-flex items-center gap-2.5 self-start text-sm font-bold uppercase hover:opacity-80 transition-opacity"
            style={{
              background: "var(--color-primary)",
              color: "#fff",
              padding: "12px 24px",
              borderRadius: "var(--radius)",
              letterSpacing: "0.12em",
              fontFamily: "var(--font-display)",
            }}
          >
            {ctaLabel}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        {/* ── RIGHT: clipping window ───────────────────────────────────── */}
        <div className="relative overflow-hidden" style={{ height: "100vh" }}>
          <div
            className="mpc-stack"
            style={{ height: `${items.length * 100}vh`, willChange: "transform" }}
          >
            {items.map((item, i) => (
              <div
                key={i}
                className="mpc-card relative overflow-hidden"
                style={{ height: "100vh", background: i % 2 === 0 ? "#241A12" : "#2E2318" }}
              >
                {item.imageUrl && (
                  <>
                    {/* Blurred, darkened backdrop fills the frame for ambiance and
                        carries the parallax drift — purely decorative, never crops
                        the subject since the sharp photo below is unaffected. */}
                    <div
                      className="mpc-card-bgblur absolute inset-0 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(${item.imageUrl})`,
                        filter: "blur(50px) brightness(0.55) saturate(1.15)",
                        transform: "scale(1.25)",
                        willChange: "transform",
                      }}
                    />
                    {/* Sharp photo, always shown in full via object-contain — never cropped */}
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="absolute inset-0 w-full h-full object-contain"
                      loading="lazy"
                    />
                  </>
                )}

                {/* Gradient overlays */}
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.38) 50%, rgba(0,0,0,0.18) 100%)" }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(0,0,0,0.28) 0%, transparent 60%)" }} />

                {/* Badge */}
                {item.badge && (
                  <div
                    className="absolute top-8 right-8 text-[9px] font-bold uppercase px-3 py-1.5 rounded-full"
                    style={{ letterSpacing: "0.28em", background: "var(--color-primary)", color: "#fff" }}
                  >
                    {item.badge}
                  </div>
                )}

                {/* Slide counter */}
                <div
                  className="absolute right-8 top-1/2 -translate-y-1/2 text-[10px] font-bold flex flex-col items-center gap-2"
                  style={{ color: "rgba(255,255,255,0.25)", writingMode: "vertical-lr", letterSpacing: "0.18em" }}
                >
                  {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                </div>

                {/* Item info */}
                <div className="absolute bottom-0 inset-x-0 px-10 pb-14 md:px-14">
                  <span
                    className="block text-xs font-bold uppercase mb-3"
                    style={{ letterSpacing: "0.28em", color: "var(--color-primary)", fontFamily: "var(--font-display)" }}
                  >
                    {item.price}
                  </span>
                  <h3
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 700,
                      fontSize: "clamp(2rem, 4vw, 3.5rem)",
                      lineHeight: 1.0,
                      color: "#fff",
                      marginBottom: "0.75rem",
                    }}
                  >
                    {item.name}
                  </h3>
                  <p
                    className="text-sm leading-relaxed max-w-sm"
                    style={{ color: "rgba(242,232,213,0.65)", fontFamily: "var(--font-sans)" }}
                  >
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Updates progress dot widths/opacity based on scroll progress [0–1]. */
function updateDots(el: HTMLElement, progress: number) {
  const dots = el.querySelectorAll<HTMLElement>(".mpc-dot");
  const n = dots.length;
  const activeIndex = Math.min(Math.floor(progress * n), n - 1);
  dots.forEach((dot, i) => {
    const isActive = i === activeIndex;
    dot.style.width = isActive ? "20px" : "7px";
    dot.style.background = isActive ? "var(--color-primary)" : "var(--color-muted)";
    dot.style.opacity = isActive ? "1" : "0.35";
  });
}
